// src/pages/ActionBoard.js

import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { IconLock, IconPlus, IconInfoCircle } from '@tabler/icons-react';

import UnitFilterContainer from '../components/filter/UnitFilterContainer';
import UnitSelectModal from '../components/UnitSelectModal';
import { ActionBoardGrid } from '../components/ActionBoardContent/ActionBoardGrid';
import { useActionBoard } from '../hooks/useActionBoard';
import {
    selectUnitId,
    selectUnits,
    setUnitId,
    selectSelectedUnitIds
} from '../features/unitsSlice';
import { selectUserData } from '../features/userSlice';

const ActionBoard = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const preselectedUnitId = queryParams.get('unitId');

    // State for unit selection modal
    const [showUnitSelectModal, setShowUnitSelectModal] = useState(false);

    // Select unitId and units from unitsSlice
    const unitId = useSelector(selectUnitId);
    const selectedUnitIds = useSelector(selectSelectedUnitIds);
    const units = useSelector(selectUnits);
    const unitsStatus = useSelector((state) => state.units.status);
    const unitsError = useSelector((state) => state.units.error);

    // Get current user data
    const userData = useSelector(selectUserData);

    const unitLeaderMap = useMemo(() => {
        const map = {};
        if (units && userData) {
            units.forEach((unit) => {
                map[String(unit.id)] = unit.unit_leaders?.includes(userData.id) || false;
            });
        }
        return map;
    }, [units, userData]);

    const leaderUnitIds = useMemo(() => {
        return Object.keys(unitLeaderMap).filter((unitId) => unitLeaderMap[unitId]);
    }, [unitLeaderMap]);

    const userName = useMemo(() => {
        if (!userData) return '';
        return userData.first_name || userData.username || '';
    }, [userData]);

    const hasSelectedUnits = selectedUnitIds.length > 0;
    const hasReadOnlySelection = hasSelectedUnits && selectedUnitIds.some((id) => !unitLeaderMap[String(id)]);

    // Check if user has access to only one unit (visibility_level === 1)
    const accessibleUnits = useMemo(() => {
        return units.filter(unit => unit.visibility_level === 1 || unit.visibility_level === 2);
    }, [units]);

    const hasMultipleAccessibleUnits = accessibleUnits.length > 1;

    // Helper function to check if user can edit the current unit
    const canEditUnit = (checkUnitId) => !!unitLeaderMap[String(checkUnitId)];

    // Get the list of selected units that the user can edit
    const editableSelectedUnits = useMemo(() => {
        return units.filter(
            (unit) => selectedUnitIds.includes(unit.id) && unitLeaderMap[String(unit.id)]
        );
    }, [units, selectedUnitIds, unitLeaderMap]);

    // Check if user can edit any of the selected units
    const canEditAnySelectedUnit = editableSelectedUnits.length > 0;

    // Handler for add action area button click
    const handleAddActionAreaClick = () => {
        if (editableSelectedUnits.length === 1) {
            // Only one editable unit selected - add directly
            handleAddActionArea(editableSelectedUnits[0].id);
        } else if (editableSelectedUnits.length > 1) {
            // Multiple editable units selected - show modal
            setShowUnitSelectModal(true);
        }
    };

    // Handler for unit selection from modal
    const handleUnitSelectForActionArea = (selectedUnitId) => {
        handleAddActionArea(selectedUnitId);
    };

    // Initialize actionBoard
    const actionBoard = useActionBoard();

    const {
        actionAreas,
        status: actionBoardStatus,
        error: actionBoardError,
        handleAddActionArea,
        isAddingActionArea,
        newActionAreaId,
        setNewActionAreaId,
        ...actionBoardHandlers
    } = actionBoard;

    useEffect(() => {
        document.title = 'TRAC - Actionboard';
    }, []);

    // Handle preselected unit from URL query params
    // Units are already fetched in Layout.js, so we just need to set the unitId if provided
    useEffect(() => {
        if (preselectedUnitId && unitsStatus === 'succeeded' && units.length > 0) {
            const preselectedUnit = units.find(unit => unit.id === preselectedUnitId);
            if (preselectedUnit && unitId !== preselectedUnitId) {
                dispatch(setUnitId(preselectedUnitId));
            }
        }
    }, [preselectedUnitId, unitsStatus, units, unitId, dispatch]);

    // Combine loading states for a single spinner
    const isLoading = unitsStatus === 'loading' || actionBoardStatus === 'loading';

    return (
        <Container className="mt-3 flex-grow-1">
            <h1 className="mt-4">
                Hallo {userName}, hier ist Dein Actionboard
            </h1>

            <h5>
                Halte hier die Handlungsfelder und Maßnahmen Deines Teams fest.
            </h5>

            <hr />

            {/* Filter section - displayed above the title */}
            {hasMultipleAccessibleUnits && (
                <>
                    {/* Filter title with info icon */}
                    <div className="d-flex align-items-center mb-2">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="filter-info-tooltip">
                                    Wähle ein oder mehrere Units aus, um deren Handlungsfelder anzuzeigen.
                                </Tooltip>
                            }
                        >
                            <span className="d-flex align-items-center" style={{ cursor: 'pointer' }}>
                                <IconInfoCircle size={20} stroke={1.8} className="me-2" style={{ color: '#000' }} />
                            </span>
                        </OverlayTrigger>
                        <h6 className="mb-0">FILTER</h6>
                    </div>

                    {/* Filter component */}
                    <div className="d-flex align-items-center mb-4">
                        <UnitFilterContainer 
                            disabledTooltip="Für diese Unit ist kein Actionboard vorhanden."
                            leaderUnitIds={leaderUnitIds}
                        />

                        {/* Read-only indicator icon - show if any selected unit is read-only */}
                        {hasSelectedUnits && hasReadOnlySelection && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="read-only-tooltip">
                                        Du hast nur Leserechte für mindestens eine ausgewählte Einheit.
                                    </Tooltip>
                                }
                            >
                                <span className="ms-2 text-secondary">
                                    <IconLock size={18} stroke={1.5} />
                                </span>
                            </OverlayTrigger>
                        )}
                    </div>
                </>
            )}

            {/* Title and Add button in the same row */}
            <div className="action-board-header mt-3">
                <Row className="align-items-center">
                    <Col>
                        <h2 className="header-title mb-0">Handlungsfelder</h2>
                    </Col>
                    <Col xs="auto">
                        {hasSelectedUnits && canEditAnySelectedUnit ? (
                            <button
                                className="btn btn-primary add-action-area-header-btn"
                                onClick={handleAddActionAreaClick}
                                disabled={isAddingActionArea}
                            >
                                Handlungsfeld +
                            </button>
                        ) : hasSelectedUnits && !canEditAnySelectedUnit ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="add-readonly-tooltip">
                                        Du hast nur Leserechte für diese Einheit.
                                    </Tooltip>
                                }
                            >
                                <span className="d-inline-block">
                                    <button
                                        className="btn btn-primary add-action-area-header-btn"
                                        disabled
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        <IconPlus size={18} className="me-1" />
                                        Hinzufügen
                                    </button>
                                </span>
                            </OverlayTrigger>
                        ) : null}
                    </Col>
                </Row>
            </div>

            {/* Show a single spinner for loading states */}
            {isLoading && (
                <div className="text-center">
                    <Spinner animation="border" role="status" className="mb-4">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}

            {/* Show error messages if applicable */}
            {!isLoading && unitsStatus === 'failed' && <p>Error: {unitsError}</p>}
            {!isLoading && actionBoardStatus === 'failed' && <p>Error: {actionBoardError}</p>}

            {/* Show message when no units are selected */}
            {!isLoading && !hasSelectedUnits && (
                <div className="text-center mt-5">
                    <p className="text-muted">Bitte wähle mindestens ein Team aus, um die Handlungsfelder anzuzeigen.</p>
                </div>
            )}

            {/* Render ActionBoardGrid once data is fetched and units are selected */}
            {!isLoading && hasSelectedUnits && actionBoardStatus === 'succeeded' && (
                <ActionBoardGrid
                    actionAreas={actionAreas}
                    handlers={{
                        ...actionBoardHandlers,
                        newActionAreaId,
                        setNewActionAreaId
                    }}
                    unitId={unitId}
                    units={units}
                    unitLeaderMap={unitLeaderMap}
                />
            )}

            {/* Unit selection modal for adding action areas */}
            <UnitSelectModal
                show={showUnitSelectModal}
                onHide={() => setShowUnitSelectModal(false)}
                units={editableSelectedUnits}
                onSelectUnit={handleUnitSelectForActionArea}
            />
        </Container>
    );
};

export default ActionBoard;